/**
 * SDR · Campos tab — schema estruturado dos campos que a IA precisa extrair
 * do lead pra montar proposta (OpenSpec #5).
 *
 * 3 sub-steps:
 *   1. Objetivo — textarea + critérios de cadastro completo + resumo
 *   2. Campos a extrair — tabela editável (key, label, required, prompt, conditional, validation, source)
 *   3. Regras & JSON — listas de regras + dados proibidos + JSON output preview
 *
 * Single config per org. Dirty flag controla habilitar "Salvar". 422 do backend
 * mapeia campo específico inline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  getProposalFieldsConfig,
  putProposalFieldsConfig,
  PutProposalFieldsError,
} from '../../services/iaProposalFields';
import { ProposalField, ProposalFieldsConfig, ProposalFieldSource } from '../../types';

type Step = 'objetivo' | 'campos' | 'regras';

interface SdrCamposTabProps {
  canEdit: boolean;
}

function makeNewField(orderHint: number): ProposalField {
  return {
    id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    key: 'novo_campo',
    label: 'Novo campo',
    required: false,
    conditional: null,
    prompt: 'O que perguntar ao lead pra coletar este campo.',
    example: '',
    source: 'manual',
    order: orderHint,
  };
}

/**
 * Parse simples de `<key> == '<value>'` (suporta aspas duplas também).
 * Retorna {key, value} ou null se não-parseável. Genérico — não assume
 * Brasil/PF/PJ; funciona com qualquer DSL key/value (country=='BR',
 * region=='EU', tier=='premium', etc).
 */
function parseConditional(expr: string | null | undefined): { key: string; value: string } | null {
  if (!expr || !expr.trim()) return null;
  const m = expr.match(/^\s*(\w+)\s*==\s*['"]([^'"]+)['"]\s*$/);
  return m ? { key: m[1], value: m[2] } : null;
}

/** Avalia se conditional é satisfeita pelo cenário (key → valor escolhido). */
function isConditionalSatisfied(
  expr: string | null | undefined,
  scenario: Record<string, string>,
): boolean {
  if (!expr || !expr.trim()) return true;
  const p = parseConditional(expr);
  if (!p) return true; // não-parseável → mostra (não bloqueia preview)
  return scenario[p.key] === p.value;
}

/**
 * Gera 1 exemplo JSON por cenário distinto de valores das chaves
 * condicionais. Ex: se `cpf.conditional='pessoa_tipo==PF'` e
 * `cnpj.conditional='pessoa_tipo==PJ'`, gera 2 exemplos (PF e PJ).
 * Genérico: funciona pra qualquer DSL key/value (country, tier, region…).
 *
 * Cap: max 4 cenários (produto cartesiano) pra não explodir UI.
 * Quando sem condicionais → 1 exemplo neutro como antes.
 */
function buildJsonPreview(fields: ProposalField[]): string {
  const sorted = fields.slice().sort((a, b) => a.order - b.order);

  // Coleta valores possíveis por key condicional.
  const valuesByKey: Record<string, Set<string>> = {};
  for (const f of sorted) {
    const p = parseConditional(f.conditional);
    if (p) {
      if (!valuesByKey[p.key]) valuesByKey[p.key] = new Set();
      valuesByKey[p.key].add(p.value);
    }
  }
  const condKeys = Object.keys(valuesByKey);

  // Sem condicionais → 1 exemplo como antes.
  if (condKeys.length === 0) {
    const obj: Record<string, string | null> = {};
    for (const f of sorted) obj[f.key] = f.example || (f.required ? 'string' : null);
    return JSON.stringify(obj, null, 2);
  }

  // Produto cartesiano dos cenários, cap em 4.
  let scenarios: Array<Record<string, string>> = [{}];
  for (const k of condKeys) {
    const vs = Array.from(valuesByKey[k]);
    const next: Array<Record<string, string>> = [];
    for (const s of scenarios) for (const v of vs) next.push({ ...s, [k]: v });
    scenarios = next;
    if (scenarios.length >= 4) {
      scenarios = scenarios.slice(0, 4);
      break;
    }
  }

  const blocks = scenarios.map((scenario) => {
    const obj: Record<string, string | null> = {};
    for (const f of sorted) {
      if (!isConditionalSatisfied(f.conditional, scenario)) continue;
      // Pra key referenciada na condicional, usa o valor do cenário em vez do example.
      if (scenario[f.key] !== undefined) {
        obj[f.key] = scenario[f.key];
      } else {
        obj[f.key] = f.example || (f.required ? 'string' : null);
      }
    }
    const header = `// Exemplo · ${Object.entries(scenario)
      .map(([k, v]) => `${k}=${v}`)
      .join(', ')}`;
    return `${header}\n${JSON.stringify(obj, null, 2)}`;
  });

  return blocks.join('\n\n');
}

const SdrCamposTab: React.FC<SdrCamposTabProps> = ({ canEdit }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<ProposalFieldsConfig | null>(null);
  const [dirty, setDirty] = useState(false);
  const [step, setStep] = useState<Step>('objetivo');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ field?: string; reason?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProposalFieldsConfig()
      .then((data) => {
        if (cancelled) return;
        setConfig(data);
      })
      .catch((err) => !cancelled && setError(err?.message || 'Erro ao carregar configuração'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const requiredCount = useMemo(
    () => (config?.fields.filter((f) => f.required && !f.conditional).length ?? 0),
    [config?.fields],
  );
  const conditionalCount = useMemo(
    () => (config?.fields.filter((f) => f.conditional).length ?? 0),
    [config?.fields],
  );
  const jsonPreview = useMemo(() => (config ? buildJsonPreview(config.fields) : ''), [config]);

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Carregando…</div>;
  }
  if (error || !config) {
    return (
      <div style={{ padding: 32, background: '#fee2e2', color: '#b91c1c', borderRadius: 8, margin: 24 }}>
        {error || 'Configuração não disponível.'}
      </div>
    );
  }

  const updateField = (id: string, patch: Partial<ProposalField>) => {
    setConfig({
      ...config,
      fields: config.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });
    setDirty(true);
  };
  const removeField = (id: string) => {
    if (!window.confirm('Remover este campo? A ação só será aplicada ao salvar.')) return;
    setConfig({ ...config, fields: config.fields.filter((f) => f.id !== id) });
    setDirty(true);
  };
  const addField = () => {
    const maxOrder = config.fields.reduce((m, f) => Math.max(m, f.order), 0);
    setConfig({ ...config, fields: [...config.fields, makeNewField(maxOrder + 1)] });
    setDirty(true);
  };

  const updateRule = (i: number, v: string) => {
    setConfig({ ...config, rules: config.rules.map((r, j) => (j === i ? v : r)) });
    setDirty(true);
  };
  const addRule = () => {
    setConfig({ ...config, rules: [...config.rules, 'Nova regra…'] });
    setDirty(true);
  };
  const removeRule = (i: number) => {
    setConfig({ ...config, rules: config.rules.filter((_, j) => j !== i) });
    setDirty(true);
  };

  const updateForbidden = (i: number, v: string) => {
    setConfig({ ...config, forbidden: config.forbidden.map((r, j) => (j === i ? v : r)) });
    setDirty(true);
  };
  const addForbidden = () => {
    setConfig({ ...config, forbidden: [...config.forbidden, 'Novo dado proibido…'] });
    setDirty(true);
  };
  const removeForbidden = (i: number) => {
    setConfig({ ...config, forbidden: config.forbidden.filter((_, j) => j !== i) });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await putProposalFieldsConfig({
        objective: config.objective,
        fields: config.fields,
        rules: config.rules,
        forbidden: config.forbidden,
        completion: config.completion,
      });
      setConfig(updated);
      setDirty(false);
    } catch (err) {
      const e = err as PutProposalFieldsError;
      setSaveError({ field: e.field, reason: e.reason || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '18px 28px 28px' }}>
      {/* Header chips + step nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>SDR · Campos</h2>
        <Chip label={`${config.fields.length} campos`} color="indigo" />
        <Chip label={`${requiredCount} obrigatórios`} color="green" />
        <Chip label={`${conditionalCount} condicionais`} color="violet" />
        <Chip label={config.version} color="slate" />
        <div style={{ flex: 1 }} />
        <StepPill active={step} onChange={setStep} />
      </div>

      {/* Notice */}
      <div
        style={{
          padding: '10px 14px',
          background: '#eef2ff',
          border: '1px solid #c7d2fe',
          borderRadius: 8,
          fontSize: 12.5,
          color: '#3730a3',
          marginBottom: 16,
        }}
      >
        <b>SDR é diferente do Atendente.</b> Aqui você não escreve markdown narrativo — define{' '}
        <i>quais campos</i> a IA precisa extrair do lead, com chave, validação, condicional e
        pergunta sugerida. O backend transforma tudo em markdown estruturado + JSON de saída usado
        pelo Field Extractor.
      </div>

      {/* Step body */}
      {step === 'objetivo' && (
        <Card>
          <Field label="Objetivo da coleta">
            <textarea
              className="textarea"
              rows={4}
              value={config.objective}
              disabled={!canEdit}
              onChange={(e) => {
                setConfig({ ...config, objective: e.target.value });
                setDirty(true);
              }}
              style={textareaStyle}
            />
            <Hint>
              Por que estamos coletando esses dados. Vai no topo do bloco SDR do prompt final.
            </Hint>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
            <div>
              <Label>Resumo</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <div>
                  Total de campos: <b>{config.fields.length}</b>
                </div>
                <div>
                  Obrigatórios: <b style={{ color: '#059669' }}>{requiredCount}</b>
                </div>
                <div>
                  Condicionais: <b style={{ color: '#8b5cf6' }}>{conditionalCount}</b>
                </div>
                <div>
                  Regras adicionais: <b>{config.rules.length}</b>
                </div>
                <div>
                  Dados proibidos: <b style={{ color: '#dc2626' }}>{config.forbidden.length}</b>
                </div>
              </div>
            </div>
            <Field label="Critérios de cadastro completo">
              <textarea
                className="textarea"
                rows={4}
                value={config.completion}
                disabled={!canEdit}
                onChange={(e) => {
                  setConfig({ ...config, completion: e.target.value });
                  setDirty(true);
                }}
                style={textareaStyle}
              />
            </Field>
          </div>
        </Card>
      )}

      {step === 'campos' && (
        <Card padding={0}>
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1.2fr 1fr 1.5fr 1fr 1fr 80px 40px',
              gap: 8,
              padding: '10px 14px',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: 10.5,
              fontWeight: 700,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            <span />
            <span>Rótulo / obrig.</span>
            <span>Chave</span>
            <span>Pergunta da IA</span>
            <span>Condicional</span>
            <span>Validação</span>
            <span>Source</span>
            <span />
          </div>
          {config.fields
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((f, idx) => (
              <FieldRow
                key={f.id}
                field={f}
                canEdit={canEdit}
                onUpdate={(patch) => updateField(f.id, patch)}
                onRemove={() => removeField(f.id)}
                rowError={saveError?.field?.startsWith(`fields[${idx}]`) ? saveError.reason : null}
              />
            ))}
          <div style={{ padding: '12px 14px' }}>
            <button
              type="button"
              disabled={!canEdit}
              onClick={addField}
              style={primaryButton}
            >
              + Adicionar campo
            </button>
          </div>
        </Card>
      )}

      {step === 'regras' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📐 Regras Adicionais</h3>
              <Chip label={`${config.rules.length}`} color="slate" />
            </div>
            <Hint>Regras de coleta. Ex.: "se PJ → CNPJ".</Hint>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {config.rules.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <span style={{ color: '#9ca3af', paddingTop: 7 }}>•</span>
                  <textarea
                    value={r}
                    disabled={!canEdit}
                    rows={1}
                    onChange={(e) => updateRule(i, e.target.value)}
                    style={{ ...textareaStyle, minHeight: 34, fontSize: 12.5 }}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => removeRule(i)}
                      style={iconBtnDanger}
                      aria-label="Remover"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {canEdit && (
                <button type="button" onClick={addRule} style={secondaryButton}>
                  + Adicionar regra
                </button>
              )}
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#dc2626' }}>
                🛡️ Dados Proibidos
              </h3>
              <Chip label={`${config.forbidden.length}`} color="rose" />
            </div>
            <Hint>
              O que a IA <b>nunca</b> deve solicitar (LGPD, segurança).
            </Hint>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {config.forbidden.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <span style={{ color: '#dc2626', paddingTop: 7 }}>⊘</span>
                  <input
                    value={r}
                    disabled={!canEdit}
                    onChange={(e) => updateForbidden(i, e.target.value)}
                    style={{ ...inputStyle, fontSize: 12.5 }}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => removeForbidden(i)}
                      style={iconBtnDanger}
                      aria-label="Remover"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {canEdit && (
                <button type="button" onClick={addForbidden} style={secondaryButton}>
                  + Adicionar item
                </button>
              )}
            </div>
          </Card>

          <Card padding={0} fullWidth>
            <div
              style={{
                padding: '12px 16px',
                background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>📤 JSON de saída esperado</h3>
              <Hint>gerado automaticamente a partir dos campos · readonly</Hint>
            </div>
            <pre
              style={{
                margin: 0,
                padding: 16,
                fontSize: 12,
                lineHeight: 1.55,
                color: '#374151',
                background: '#fff',
                whiteSpace: 'pre-wrap',
                fontFamily: 'JetBrains Mono, monospace',
              }}
            >
              {jsonPreview}
            </pre>
          </Card>
        </div>
      )}

      {/* Save bar — OpenSpec unify-ia-settings Fase 1: wizard com
       * Próximo nos steps 1+2, Salvar só no step 3 (último), Voltar
       * nos steps 2+3 sem perder dados. */}
      <div
        style={{
          marginTop: 20,
          padding: '14px 16px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          Versão: <b>{config.version}</b>
          {dirty ? ' · mudanças não salvas' : ' · sincronizado'}
        </span>
        <div style={{ flex: 1 }} />
        {saveError && !saveError.field && (
          <span style={{ color: '#b91c1c', fontSize: 12.5 }}>{saveError.reason}</span>
        )}
        {step !== 'objetivo' && (
          <button
            type="button"
            onClick={() => setStep(step === 'regras' ? 'campos' : 'objetivo')}
            style={secondaryButton}
          >
            ← Voltar
          </button>
        )}
        {step !== 'regras' && (
          <button
            type="button"
            onClick={() => setStep(step === 'objetivo' ? 'campos' : 'regras')}
            style={primaryButton}
          >
            Próximo →
          </button>
        )}
        {step === 'regras' && canEdit && (
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            style={{ ...primaryButton, opacity: !dirty || saving ? 0.5 : 1 }}
          >
            {saving ? 'Salvando…' : 'Salvar & publicar'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Components & styles ───────────────────────────────────────────────────

function StepPill({ active, onChange }: { active: Step; onChange: (s: Step) => void }) {
  const items: { id: Step; label: string }[] = [
    { id: 'objetivo', label: '1 · Objetivo' },
    { id: 'campos', label: '2 · Campos a extrair' },
    { id: 'regras', label: '3 · Regras & JSON' },
  ];
  return (
    <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 2 }}>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          style={{
            padding: '6px 14px',
            border: 'none',
            background: active === it.id ? '#fff' : 'transparent',
            color: active === it.id ? '#111827' : '#6b7280',
            fontWeight: active === it.id ? 700 : 500,
            fontSize: 12.5,
            borderRadius: 6,
            cursor: 'pointer',
            boxShadow: active === it.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function Card({
  children,
  padding = 18,
  fullWidth = false,
}: {
  children: React.ReactNode;
  padding?: number;
  fullWidth?: boolean;
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding,
        gridColumn: fullWidth ? '1 / -1' : undefined,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
      {children}
    </label>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6 }}>{children}</div>;
}

function Chip({ label, color }: { label: string; color: 'indigo' | 'green' | 'violet' | 'slate' | 'rose' }) {
  const bg =
    color === 'indigo'
      ? '#e0e7ff'
      : color === 'green'
      ? '#dcfce7'
      : color === 'violet'
      ? '#ede9fe'
      : color === 'rose'
      ? '#ffe4e6'
      : '#f1f5f9';
  const fg =
    color === 'indigo'
      ? '#4338ca'
      : color === 'green'
      ? '#166534'
      : color === 'violet'
      ? '#6d28d9'
      : color === 'rose'
      ? '#b91c1c'
      : '#475569';
  return (
    <span
      style={{
        background: bg,
        color: fg,
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 11.5,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function FieldRow({
  field,
  canEdit,
  onUpdate,
  onRemove,
  rowError,
}: {
  field: ProposalField;
  canEdit: boolean;
  onUpdate: (patch: Partial<ProposalField>) => void;
  onRemove: () => void;
  rowError?: string | null;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1.2fr 1fr 1.5fr 1fr 1fr 80px 40px',
        gap: 8,
        padding: '10px 14px',
        borderBottom: '1px solid #f1f5f9',
        alignItems: 'flex-start',
        background: rowError ? '#fee2e2' : undefined,
      }}
    >
      <span style={{ color: '#cbd5e1', paddingTop: 7, fontSize: 14 }}>⋮⋮</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <input
          value={field.label}
          disabled={!canEdit}
          onChange={(e) => onUpdate({ label: e.target.value })}
          style={inputStyle}
        />
        <label style={{ fontSize: 11, color: '#475569', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={field.required}
            disabled={!canEdit}
            onChange={(e) => onUpdate({ required: e.target.checked })}
          />{' '}
          obrigatório
        </label>
      </div>
      <input
        value={field.key}
        disabled={!canEdit}
        onChange={(e) => onUpdate({ key: e.target.value })}
        style={{ ...inputStyle, fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}
      />
      <textarea
        rows={2}
        value={field.prompt}
        disabled={!canEdit}
        onChange={(e) => onUpdate({ prompt: e.target.value })}
        style={{ ...textareaStyle, minHeight: 50, fontSize: 12 }}
      />
      <input
        value={field.conditional || ''}
        disabled={!canEdit}
        placeholder="ex: pessoa_tipo == 'PF'"
        onChange={(e) => onUpdate({ conditional: e.target.value || null })}
        style={{ ...inputStyle, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}
      />
      <input
        value={field.validation || ''}
        disabled={!canEdit}
        placeholder="ex: CPF válido"
        onChange={(e) => onUpdate({ validation: e.target.value })}
        style={{ ...inputStyle, fontSize: 11 }}
      />
      <select
        value={field.source}
        disabled={!canEdit}
        onChange={(e) => onUpdate({ source: e.target.value as ProposalFieldSource })}
        style={{ ...inputStyle, fontSize: 11 }}
      >
        <option value="manual">manual</option>
        <option value="ai">✦ IA</option>
      </select>
      {canEdit ? (
        <button type="button" onClick={onRemove} style={iconBtnDanger} aria-label="Remover">
          ✕
        </button>
      ) : (
        <span />
      )}
      {rowError && (
        <div style={{ gridColumn: '2 / -1', color: '#b91c1c', fontSize: 11.5, marginTop: 4 }}>
          {rowError}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 9px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 12.5,
  fontFamily: 'inherit',
  outline: 'none',
};

const textareaStyle: React.CSSProperties = {
  padding: '7px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  resize: 'vertical',
};

const primaryButton: React.CSSProperties = {
  padding: '8px 16px',
  background: '#4338ca',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};

const secondaryButton: React.CSSProperties = {
  alignSelf: 'flex-start',
  padding: '6px 12px',
  background: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  marginTop: 4,
};

const iconBtnDanger: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#dc2626',
  cursor: 'pointer',
  fontSize: 14,
  padding: '6px 8px',
  alignSelf: 'flex-start',
};

export default SdrCamposTab;
